import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ActivityEditable,
  ActivityEntry,
  ActivityHistoryResponse,
  ActivityPassLine,
  fetchActivityHistory,
  fetchActivityRecord,
  updateActivityRecord,
} from '../../api/activity-client';
import { useAuth } from '../../context/auth-provider';
import { resolvePostAuthRoute } from '../../utils/post-auth-route';
import { can, PERMISSIONS } from '../../auth/permissions';
import { normalizeUserRole, roleLabel } from '../../auth/roles';
import { toast } from '../../utils/toast';
import {
  palette,
  radius,
  spacing,
  typography,
  status,
} from '../../style-utils/theme';
import { Text, font } from '../../style-utils/text';
import { down, touch } from '../../style-utils/media-queries';
import { dateTimeIn } from '../../function-utils/building-time';
import { useClock } from '../../context/clock-context';
import { BothClocks, ClockNote } from '../clock-note/clock-note';
import { listItemMotion, m } from '../../motion';
import { signVisitorOut } from '../../api/visitor-log-client';
import { closeActivity } from '../../api/daily-log-client';
import {
  getPackage,
  releasePackage,
  Package,
} from '../../api/packages-client';
import { PARCEL_HANDOVER_PATH } from '../pages/main-menu-model';
import { StatusPill } from '../status-pill/status-pill';
import { AppButton } from '../button/app-button';
import { PassDetailModal } from '../pass-editor/pass-detail-modal';

// ONE RECORD, OPENED DIRECTLY. Brief §13, §20 and §11.
//
// Before this there was NO detail route for a visitor entry, a parcel or a log
// row anywhere in the product — so a handover could say "a guest was admitted to
// unit 10" and the only way to see it was to go and search a list by hand. This
// is the destination those citations point at.
//
// IT IS A REAL ROUTE, not a modal, and that is the requirement rather than a
// preference: the brief asks that a direct link survive a refresh and be
// shareable. /record/:source/:id does; a modal over a list does not.
//
// THE HISTORICAL SNAPSHOT AND THE CURRENT RECORD ARE DIFFERENT THINGS (brief
// §16). A handover shows what was true when the shift ended and never changes.
// THIS shows the record as it stands now — which is exactly why the link is
// useful: a visitor who was "still inside" at handover may have left since, and
// the two views disagreeing is the point, not a bug.

/* $embedded = hosted in a modal, which already supplies the width, the padding
   and the scrolling. Keeping the page's own 780px cap and 64px bottom padding
   inside a card produces a narrow column floating in a wider card with a hole
   under it. Tokens on both paths — the raw px here predate the design system and
   are replaced rather than duplicated. */
const Page = styled.main<{ $embedded?: boolean }>`
  max-width: ${({ $embedded }) => ($embedded ? 'none' : '780px')};
  margin: 0 auto;
  padding: ${({ $embedded }) =>
    $embedded ? '0' : `${spacing.md} ${spacing.sm} ${spacing.xxxl}`};
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
  overflow-wrap: anywhere;

  ${down('sm')} {
    padding: ${({ $embedded }) =>
      $embedded ? '0' : `${spacing.sm} ${spacing.xs} ${spacing.xxl}`};
  }
`;

const Back = styled.button`
  align-self: flex-start;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${palette.lagoon};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${palette.iris};
    outline-offset: 2px;
  }

  ${touch} {
    min-height: 44px;
  }
`;

/* NO PAGE-LEVEL ENTRANCE HERE: standalone, the route layer moves the page;
   embedded, the modal moves the layer. The sections inside arrive with a
   short cascade instead — `sectionMotion(i)`, capped at SECTION_CAP so a
   record with many events does not spend a second unfolding. */
const SECTION_CAP = 6;
const sectionMotion = (index: number) => ({
  ...listItemMotion(Math.min(index, SECTION_CAP)),
  layout: false as const,
});

const Card = m.section`
  border: 1px solid ${palette.line};
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Chip = styled.span<{ tone?: 'open' }>`
  display: inline-block;
  font-size: ${typography.size.xxs}px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
  margin-right: 8px;
  /* status.warning.fg on the amber wash: palette.warning on warningSoft is
     1.92:1. Same fix as the analytics state pill. */
  color: ${({ tone }) => (tone === 'open' ? status.warning.fg : palette.irisDeep)};
  background: ${({ tone }) =>
    tone === 'open' ? palette.warningSoft : palette.irisTint};
`;

const Rows = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px 16px;

  ${down('sm')} {
    grid-template-columns: 1fr;
    gap: ${spacing.half};

    dd {
      margin-bottom: 8px;
    }
  }

  dt {
    font-size: 12px;
    color: ${palette.slateLight};
  }

  dd {
    margin: 0;
    font-size: 14px;
  }
`;

const HistoryItem = styled.li`
  padding: 8px 0;
  border-top: 1px solid ${palette.line};
  font-size: 13px;

  &:first-of-type {
    border-top: none;
  }
`;

const Was = styled.span`
  text-decoration: line-through;
  color: ${palette.slateLight};
`;

const EditGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;

  ${down('sm')} {
    grid-template-columns: 1fr;
  }
`;

const LabelledField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: ${palette.slateLight};
  min-width: 0;

  input,
  textarea {
    font: inherit;
    font-size: 14px;
    color: ${palette.ink};
    padding: 8px;
    border: 1px solid ${palette.line};
    border-radius: 6px;
    width: 100%;
    /* iOS Safari zooms the page when a focused field is under 16px. */
    ${touch} {
      font-size: 16px;
    }
  }

  textarea {
    min-height: 72px;
    resize: vertical;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Primary = styled.button`
  border: none;
  background: ${palette.lagoon};
  color: ${palette.white};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  ${touch} {
    min-height: 44px;
  }
`;

const Ghost = styled(Primary)`
  background: transparent;
  color: ${palette.slate};
  border: 1px solid ${palette.line};
`;

const Muted = styled.p`
  margin: 0;
  color: ${palette.slateLight};
  font-size: 13px;
`;

const SOURCE_LABEL: Record<string, string> = {
  visitor_log: 'Visitor entry',
  daily_log: 'Log entry',
  package: 'Parcel',
};

// A field name from entry_field_edits, in the words the screen uses elsewhere.
const FIELD_LABEL: Record<string, string> = {
  name: 'First name',
  middleName: 'Middle name',
  lastName: 'Last name',
  names: 'First name',
  middleNames: 'Middle name',
  lastNames: 'Last name',
  details: 'Details',
  description: 'Description',
  notes: 'Notes',
  additionalNotes: 'Notes',
  apartmentLocation: 'Unit',
  unit_label: 'Unit',
  carrier: 'Carrier',
  shelf: 'Shelf',
  parcel_count: 'Number of parcels',
  recipient_name: 'Recipient',
  released_to: 'Released to',
};

const EDIT_LABEL: Record<string, string> = {
  buildingActivity: 'Activity',
  visitorType: 'Visitor type',
  visitorDetails: 'Details',
  priority: 'Priority',
  details: 'Details',
  description: 'Description',
  notes: 'Notes',
  additionalNotes: 'Additional notes',
  apartmentLocation: 'Unit',
  approval: 'Approval',
  parkingValidation: 'Parking validated',
  preAuthorized: 'Pre-authorised',
  numberOfGuests: 'Number of guests',
  // ---- THE PARCEL COLUMNS ----
  // These are the `packages` table's own names, because the patch goes back to
  // PUT /packages/:id exactly as the server sent it. `unit_label` is the same
  // fact the other two books call apartmentLocation, and it gets the same word
  // here so a reader is not asked to learn a second name for a unit.
  unit_label: 'Unit',
  carrier: 'Carrier',
  shelf: 'Shelf',
  parcel_count: 'Number of parcels',
  recipient_name: 'Recipient',
  released_to: 'Released to',
};

// The long ones get a textarea; everything else a single line.
const LONG_FIELDS = new Set([
  'details', 'description', 'notes', 'additionalNotes', 'visitorDetails',
]);

/* THE CONFIRMATION PANEL — what is about to change, before it changes.
 *
 * Inset rather than a modal: a second layer over a record that is already in a
 * layer stacks two scrims, and the thing being confirmed is the form directly
 * above it. Keeping them on one surface means the reader can look up and check.
 */
const Diff = styled.div`
  margin-top: ${spacing.sm};
  padding: ${spacing.sm};
  background: ${palette.surfaceMuted};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
`;

/* One field: its name, the value being replaced, and the value replacing it.
 *
 * The OLD value is struck through and quiet; the NEW one is ink. Colour is not
 * the only signal — the strike-through survives a monochrome screen and a
 * reader who cannot distinguish the two greens (design_system §7). */
const DiffRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${spacing.xs};
  padding: ${spacing.half} 0;
  font-size: ${typography.size.xs}px;
  overflow-wrap: anywhere;

  & + & {
    border-top: 1px solid ${palette.line};
  }

  > b {
    flex: 0 0 auto;
    font-weight: ${typography.weight.semibold};
    color: ${palette.slate};
  }

  > s {
    color: ${palette.slateLight};
  }

  > span {
    color: ${palette.ink};
    font-weight: ${typography.weight.medium};
  }

  /* The arrow between old and new, drawn rather than typed so it is not read
     aloud as a word by a screen reader walking the two values. */
  > s + span::before {
    content: '→ ';
    color: ${palette.slateLight};
  }
`;

/* "7 Oct 2026, 14:05" on the record's clock. The zone is ALWAYS passed: this
   used to be called with none, so the modal printed the device's time over a
   desk feed that had just shown the building's for the same row. */
const when = (iso: string, tz: string): string => dateTimeIn(iso, tz);

/**
 * WHETHER THE PERSON WHO TOOK THE PARCEL WAS A RESIDENT, in the words the owner
 * used: "which user or NON USER picked up the parcel for a specific user".
 *
 * `released_to_resolution` (migration 042) is the stored answer:
 * 'single_match' / 'picked' mean the name was matched to a real occupancy on the
 * unit, 'no_match' means it was typed and matched nobody, 'unset' means nothing
 * was resolved at all — which the CHECK on `packages` forbids once a parcel is
 * picked up, so it can only appear on a legacy row.
 *
 * ONLY THE WEAKER STATES ARE MARKED, which is the convention iq-answer.tsx
 * already follows: tagging every matched row too is noise, and an unmarked name
 * is the case the desk expects. A neighbour or a courier collecting for somebody
 * is a legitimate and common thing — this is a fact about the record, not a
 * warning about the person.
 */
const collectorNote = (resolution?: string | null): string => {
  if (resolution === 'no_match') return ' — not a resident';
  if (!resolution || resolution === 'unset') return ' — not matched to a resident';

  return '';
};

/**
 * ONE COMPONENT, TWO HOSTS.
 *
 * A record is reached two ways: the deep link (/record/:source/:id, so a row can
 * be pasted to a colleague and survive a refresh) and the feed's own "Open",
 * which raises it over the list. Those want the same content and different
 * chrome — the page needs a Back control, the modal already has a close button
 * and a scrim.
 *
 * So the ids and the dismiss action are PROPS with route fallbacks rather than a
 * second copy of this component. `useParams` returns empty strings outside a
 * matched route, which is exactly the "no route, use the props" case, and
 * `onClose` replaces the Back button when the host supplies one.
 */
/* ═══════════ TAKE IT OUT, FROM THE RECORD ITSELF ═══════════
 *
 * Owner instruction, 2026-09-11: "if its an in record there should be an easy
 * way to create an out, so i can take visitors and parcels out directly from
 * the main menu instead of going into their dedicated in out routes and this
 * must appear in the dashboard as a record as well, like a regular in an out".
 *
 * SAME WRITE PATHS AS THE DESK, NOTHING NEW. A visitor goes through POST
 * /visitor-log/:id/exit, an activity through POST /daily-log/:id/close, a
 * parcel through POST /packages/:id/release — the exact calls the dedicated
 * out screens make — so the out lands as the same row, with the same stamp and
 * author, in the feed, the dashboard and the handover.
 *
 * Offered only for an "in" that is STILL OPEN, and only to a reader holding the
 * permission that route checks. The server refuses regardless; this is the
 * affordance, never the gate.
 *
 * ONE PRESS TO ASK, ONE TO CONFIRM. A departure cannot be un-recorded from this
 * screen, so the second press names exactly what will be written.
 *
 * A PARCEL keeps the wrong-person guard. The easy path is "collected by the
 * person it is for", which reuses the resolution a human already made for the
 * recipient; anybody else goes through the parcel desk's picker, and a parcel
 * whose recipient was never identified is sent there too.
 */
type OutKind = 'visitor' | 'activity' | 'parcel';

const serverMessage = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { message?: string } }; message?: string };

  return e?.response?.data?.message || e?.message || fallback;
};

const QuickOut: React.FC<{
  source: string;
  id: string;
  events: ActivityEntry[];
  order: number;
  onDone: () => void;
}> = ({ source, id, events, order, onDone }) => {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pkg, setPkg] = useState<Package | null>(null);

  // The event that is still in: an arrival with no departure, an activity not
  // closed, a parcel still on the shelf.
  const arrival = events.find(
    e =>
      e.direction === 'in' &&
      e.open === true &&
      (source !== 'package' || !e.parcelEvent || e.parcelEvent === 'arrival')
  );
  const kind: OutKind | null = !arrival
    ? null
    : source === 'visitor_log'
      ? 'visitor'
      : source === 'daily_log'
        ? 'activity'
        : source === 'package'
          ? 'parcel'
          : null;
  const allowed =
    kind === 'visitor'
      ? can(permissions, PERMISSIONS.VISITORLOG_CREATE)
      : kind === 'activity'
        ? can(permissions, PERMISSIONS.DAILYLOG_CREATE)
        : kind === 'parcel'
          ? can(permissions, PERMISSIONS.PACKAGE_RELEASE)
          : false;

  useEffect(() => {
    if (kind !== 'parcel' || !allowed) return undefined;
    let alive = true;
    getPackage(id)
      .then(p => {
        if (alive) setPkg(p);
      })
      .catch(() => {
        if (alive) setError('We could not read this parcel just now.');
      });

    return () => {
      alive = false;
    };
  }, [kind, allowed, id]);

  if (!kind || !allowed || !arrival) return null;

  const who = arrival.people.filter(Boolean).join(', ');
  const recipient = pkg?.recipient_name || arrival.parcelRecipient || '';
  const linked =
    !!pkg &&
    (pkg.recipient_resolution === 'single_match' ||
      pkg.recipient_resolution === 'picked');
  const copy =
    kind === 'visitor'
      ? {
          title: 'Sign out',
          lead: who ? `Record that ${who} left, now.` : 'Record the departure, now.',
          press: 'Sign out now',
          confirm: who ? `Confirm: sign ${who} out` : 'Confirm: sign out',
          done: 'Signed out',
        }
      : kind === 'activity'
        ? {
            title: 'Close this activity',
            lead: 'Record that it finished, now.',
            press: 'Close now',
            confirm: 'Confirm: close it now',
            done: 'Activity closed',
          }
        : {
            title: 'Hand this parcel over',
            lead: recipient
              ? `For ${recipient}. If they are collecting it themselves, it is one press.`
              : 'Record who collected it.',
            press: recipient ? `Collected by ${recipient}` : 'Hand over',
            confirm: `Confirm: handed to ${recipient || 'the recipient'}`,
            done: 'Parcel handed over',
          };

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      if (kind === 'visitor') {
        await signVisitorOut(Number(id));
      } else if (kind === 'activity') {
        await closeActivity(Number(id));
      } else if (pkg) {
        // The COLLECTOR is the recipient, so the collector's resolution is the
        // recipient's: a linked resident stays linked ('picked' — a person
        // chose it), and a recipient recorded as "not a resident" is handed
        // over as exactly that.
        await releasePackage(id, {
          released_to: recipient,
          released_to_occupancy_id: linked ? pkg.recipient_occupancy_id : null,
          released_to_resolution: linked ? 'picked' : 'no_match',
        });
      }
      toast.success(copy.done);
      setConfirming(false);
      onDone();
    } catch (e) {
      const msg = serverMessage(e, 'We could not record that just now.');
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const unreleasable = kind === 'parcel' && !!pkg && !pkg.can_release;

  return (
    <Card {...sectionMotion(order)} aria-label={copy.title}>
      <Text mt={0} mb={0} fontSize={16} fontWeight={700} fontFamily={font.family}>
        {copy.title}
      </Text>
      {unreleasable ? (
        <Muted>
          Nobody has said who this parcel is for yet, so it cannot be handed over
          from here. The parcel desk records that first.
        </Muted>
      ) : (
        <Muted>{copy.lead}</Muted>
      )}
      <Actions>
        {unreleasable ? null : !confirming ? (
          <Primary
            type="button"
            disabled={busy || (kind === 'parcel' && !pkg)}
            onClick={() => setConfirming(true)}
          >
            {kind === 'parcel' && !pkg && !error ? 'Checking the parcel…' : copy.press}
          </Primary>
        ) : (
          <>
            <Primary type="button" disabled={busy} onClick={() => void run()}>
              {busy ? 'Saving…' : copy.confirm}
            </Primary>
            <Ghost type="button" disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </Ghost>
          </>
        )}
        {kind === 'parcel' ? (
          <Ghost type="button" onClick={() => navigate(PARCEL_HANDOVER_PATH)}>
            {unreleasable ? 'Open the parcel desk' : 'Someone else collected it'}
          </Ghost>
        ) : null}
      </Actions>
      {error ? <Muted role="alert">{error}</Muted> : null}
    </Card>
  );
};

export interface RecordDetailProps {
  /** Omitted on the route; supplied when raised as a modal. */
  source?: string;
  id?: string;
  /** Present = hosted in a layer. The Back control is not drawn. */
  onClose?: () => void;
  /** The record was changed from here (an out was taken) — the host re-reads
   *  its own list so the new row is there when the layer closes. */
  onChanged?: () => void;
}

export const RecordDetail: React.FC<RecordDetailProps> = ({
  source: sourceProp,
  id: idProp,
  onClose,
  onChanged,
}) => {
  const params = useParams();
  const source = sourceProp ?? params.source ?? '';
  const id = idProp ?? params.id ?? '';
  const navigate = useNavigate();
  const [events, setEvents] = useState<ActivityEntry[] | null>(null);
  const [history, setHistory] = useState<ActivityHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editable, setEditable] = useState<ActivityEditable | null>(null);
  const [draft, setDraft] = useState<ActivityEditable>({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  // The confirmation step sits between Edit and the write; see pendingChanges.
  const [reviewing, setReviewing] = useState(false);
  // A visitor arrival signed in on a visitor pass (Plan 3), and whether the
  // pass is open over this record.
  const [passLine, setPassLine] = useState<ActivityPassLine | null>(null);
  const [passOpen, setPassOpen] = useState(false);
  /* THE RECORD'S CLOCK (R-T12) — and its building's own zone, for the second
     line. The detail takes the clock of the SURFACE THAT OPENED IT: raised over
     the desk feed it is the building's, for whoever is at the desk; over a
     dashboard table, or by direct link, it is the clock the answer carries. */
  const [answered, setAnswered] = useState<{
    tz?: string;
    tz_source?: string;
    buildingId: number | null;
    buildingZone: string | null;
  } | null>(null);
  const clock = useClock({
    buildingId: answered?.buildingId,
    buildingZone: answered?.buildingZone,
    response: answered,
  });

  const {
    permissions,
    user,
    onboarding,
  }: {
    permissions: string[];
    // Needed by goBack below: a link opened in a fresh tab has no history, so
    // "back" has to resolve to this reader's own home rather than to nowhere.
    user: ReturnType<typeof useAuth>['user'];
    onboarding: ReturnType<typeof useAuth>['onboarding'];
  } = useAuth();
  // MIRRORS THE SERVER'S OWN GATE, not a role list that happens to line up
  // today: the PUT endpoints require dailylog:create / visitorlog:create. A
  // reader without it simply does not see the affordance — and the server
  // refuses regardless, because this is presentation, never enforcement.
  const mayEdit =
    (source === 'daily_log' && can(permissions, PERMISSIONS.DAILYLOG_CREATE)) ||
    (source === 'visitor_log' && can(permissions, PERMISSIONS.VISITORLOG_CREATE)) ||
    // PARCELS. Owner report, 2026-08-14: "I am still unable to edit records",
    // on a parcel. This clause was simply missing — and so was the server's
    // editable-field list — so the one source with a full PUT route behind it
    // was the one record type the editor never offered.
    //
    // `package:edit` is the permission that route actually requires, not
    // package:create: taking a parcel in and correcting one afterwards are
    // different acts and the role matrix already separates them.
    (source === 'package' && can(permissions, PERMISSIONS.PACKAGE_EDIT));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const record = await fetchActivityRecord(source, id);
      setEvents(record.events);
      setAnswered({
        tz: record.tz,
        tz_source: record.tz_source,
        buildingId: record.building?.id ?? record.events[0]?.buildingId ?? null,
        buildingZone: record.building?.tz ?? record.events[0]?.tz ?? null,
      });
      setPassLine(record.pass && record.pass.on_pass ? record.pass : null);
      setEditable(record.editable);
      setDraft(record.editable || {});
      setEditing(false);
      setReviewing(false);
      // The history table is keyed on the LOG table name, and `package` is
      // `packages` there. A record with no history is the normal case and
      // answers 200 with an empty trail, so a failure here is a real failure.
      try {
        setHistory(
          await fetchActivityHistory(
            source === 'package' ? 'packages' : source,
            id
          )
        );
      } catch {
        setHistory(null);
      }
    } catch {
      // A 404 covers both "no such record" and "not yours" — deliberately
      // indistinguishable, so this endpoint cannot be used to discover which
      // ids exist in another organisation.
      setError('This record is not available.');
      setEvents(null);
    } finally {
      setLoading(false);
    }
  }, [source, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const primary = events && events.length ? events[0] : null;

  const save = async () => {
    // 'package' BELONGS HERE. Everything around this line was already built for
    // parcels — mayEdit has a PACKAGE_EDIT clause (:411), EDIT_LABEL carries the
    // parcel columns, updateActivityRecord routes 'package' to PUT /packages/:id
    // (activity-client.ts:259-271), and the server has the editable-field list.
    // Only this guard was left behind, so a concierge correcting a mis-keyed
    // recipient pressed "Confirm and save" and got no request, no toast and no
    // error — the dialog just sat there. That is the whole of "I am still unable
    // to edit records" on a parcel.
    if (
      source !== 'daily_log' &&
      source !== 'visitor_log' &&
      source !== 'package'
    ) {
      return;
    }

    // Only what actually CHANGED. The PUT endpoints accept a partial body and
    // append one entry_field_edits row per changed field, so sending the whole
    // form would write a history entry for every untouched field and bury the
    // real correction in noise.
    const patch: ActivityEditable = {};
    Object.keys(draft).forEach(k => {
      if ((draft[k] ?? '') !== (editable?.[k] ?? '')) patch[k] = draft[k];
    });

    if (!Object.keys(patch).length) {
      setEditing(false);
      setReviewing(false);

      return;
    }

    setSaving(true);
    try {
      await updateActivityRecord(source, id, patch);
      toast.success('Saved');
      // Reload rather than patching local state: the edit history below has
      // just gained a row, and showing a record whose trail is one save behind
      // is exactly the kind of quiet lie this screen exists to prevent.
      await load();
    } catch (error) {
      // An arrival on a visitor pass cannot outgrow its pass: the server says
      // how many are left, in its own words (409 PASS_PARTY_FULL).
      const data = (error as { response?: { status?: number; data?: { code?: string; message?: string } } })
        ?.response?.data;
      toast.error(
        data?.code === 'PASS_PARTY_FULL' && typeof data.message === 'string' && data.message
          ? data.message
          : 'We could not save that change.'
      );
    } finally {
      setSaving(false);
    }
  };

  // ---- WHAT IS ABOUT TO CHANGE, BEFORE IT CHANGES ----
  //
  // "I need to be able to safely edit the records because mistakes happen"
  // (owner, 2026-08-13). Correcting an entry was already SAFE in the sense that
  // matters most — the PUT writes one entry_field_edits row per changed field,
  // that table is append-only for every role (lb_efe_append_only, migration
  // 041), and the previous value is kept and shown in the trail below. Nothing
  // is destroyed by an edit.
  //
  // What was missing is the part a person can see. Save went straight through,
  // so a mis-keyed field became a permanent, attributed correction with no
  // moment to catch it — and the reader had no way of knowing the old value
  // survives, which is the fact that makes editing feel safe rather than final.
  //
  // So: the same partial patch as before, shown FIELD BY FIELD with its old and
  // new value, confirmed once. Derived from the draft rather than tracked as it
  // is typed, so it cannot disagree with what the request will carry.
  const pendingChanges = Object.keys(draft)
    .filter(k => (draft[k] ?? '') !== (editable?.[k] ?? ''))
    .map(k => ({
      field: k,
      label: EDIT_LABEL[k] || k,
      from: (editable?.[k] ?? '') as string,
      to: (draft[k] ?? '') as string,
    }));

  /**
   * BACK, FOR A LINK THAT WAS BUILT TO BE PASTED.
   *
   * This route exists so a citation in a handover survives a refresh and can be
   * shared — which means it is routinely opened in a FRESH TAB, where
   * `navigate(-1)` has nowhere to go: the browser either sits still or leaves
   * the application entirely. The screen also renders bare, with no shell, so
   * that dead control was the only navigation on it.
   *
   * `window.history.state.idx` is react-router's own entry index: 0 means this
   * is the first entry in this tab, so there is no in-app history to go back to
   * and the honest offer is the reader's home screen instead.
   */
  const cameFromInsideTheApp =
    typeof window !== 'undefined' && (window.history.state?.idx ?? 0) > 0;
  const goBack = () => {
    if (cameFromInsideTheApp) {
      navigate(-1);

      return;
    }

    navigate(resolvePostAuthRoute(user, onboarding, permissions || []), {
      replace: true,
    });
  };

  return (
    /* TWO <main> LANDMARKS AT ONCE, MEASURED. This component is BOTH a route
       (/record/:source/:id, where it is the page) and a modal opened over the
       Records tab and the activity feed — and those hosts already render the
       app's one <main> through ContentWrapper. Embedded, it therefore added a
       second one: `main x2 ["main-content","(no id)"]` in
       `probe-wp39-landmarks.js` with the record modal open. A document with two
       main landmarks has, to a screen reader, no main landmark at all.

       Embedded it is a <section> named for the record it is showing, which is
       what it actually is. Standalone it stays the page's <main>, and now
       carries the skip link's target id like every other page-level main. */
    <Page
      $embedded={!!onClose}
      as={onClose ? 'section' : 'main'}
      id={onClose ? undefined : 'main-content'}
      tabIndex={onClose ? undefined : -1}
      aria-label={onClose ? 'Record details' : undefined}
    >
      {/* The modal host draws its own close control and its own scrim, so a
          second dismiss affordance inside the card is one control too many —
          and `navigate(-1)` in a layer would take the whole page back rather
          than closing the layer. */}
      {onClose ? null : (
        <Back type="button" onClick={goBack}>
          {cameFromInsideTheApp ? '← Back' : '← Go to your home screen'}
        </Back>
      )}

      {loading && <Muted>Loading…</Muted>}

      {error && (
        <Card {...sectionMotion(0)}>
          <Text mt={0} mb={0} fontSize={16} fontWeight={700} fontFamily={font.family}>
            Related record unavailable
          </Text>
          {/* Brief §19: never the raw reference as a fallback. */}
          <Muted>
            It may have been removed, or it belongs to a building you do not have
            access to.
          </Muted>
        </Card>
      )}

      {primary && (
        <>
          <Text mt={0} mb={0} fontSize={20} fontWeight={700} fontFamily={font.family}>
            {SOURCE_LABEL[primary.source] || 'Record'}
          </Text>
          {/* Which clock every time on this record is on. One note for the
              record; where the reader's clock is not the building's, each
              event below carries the building's time as a second line. */}
          <ClockNote clock={clock} />

          {events?.map((event, i) => (
            <Card key={`${event.direction || 'e'}-${i}`} {...sectionMotion(i + 1)}>
              <div>
                <Chip>
                  {event.groupLabel}
                  {event.direction ? ` ${event.direction}` : ''}
                </Chip>
                {/* The same state, named per subject — as the feed row names
                    it. The record now carries `open` for every source, and a
                    parcel on the shelf is not "still inside". */}
                {event.open === true && (
                  <Chip tone="open">
                    {event.source === 'daily_log'
                      ? 'Still open'
                      : event.source === 'package'
                        ? 'Not collected'
                        : 'Still inside'}
                  </Chip>
                )}
              </div>
              <Rows>
                <dt>When</dt>
                <dd>
                  {when(event.at, clock.zone)}
                  <BothClocks
                    iso={event.at}
                    clock={clock}
                    buildingZone={event.tz || answered?.buildingZone}
                  />
                </dd>

                {/* ── A PARCEL HAS TWO PEOPLE AND THEY ARE NOT INTERCHANGEABLE ──
                    `people` is one unlabelled, de-duplicated array, so this
                    printed "Parcel for: Nils Sorensen, Gjorge Lazarov" — the
                    addressee and whoever collected it, under one heading that
                    claims both are the addressee. And a resident collecting
                    their own parcel de-duplicates to a single name, so the
                    record said nothing about the handover at all.
                    Owner instruction, 2026-08-21: "picked up by has to be always
                    visible everywhere so we know which user or non user picked
                    up the parcel for a specific user." */}
                {event.source === 'package' ? (
                  <>
                    {event.parcelRecipient && (
                      <>
                        <dt>Parcel for</dt>
                        <dd>{event.parcelRecipient}</dd>
                      </>
                    )}
                    {/* ALWAYS RENDERED ON THE HANDOVER, never conditional on a
                        value being present. A missing collector is the fact a
                        supervisor most needs — the desk released a parcel and
                        recorded nobody — and a row that disappears when the
                        answer is bad is a row that only ever brings good news. */}
                    {/* A PARCEL LEAVES IN ONE OF TWO WAYS and the record must
                        say which. Both are `direction: 'out'` and both are
                        `kind: 'Parcel out'` — the object left either way — so
                        `parcelEvent` is what separates them. Printing "Picked up
                        by: Not recorded" over a parcel that went back to the
                        carrier would report a missing handover where there was
                        never meant to be one. */}
                    {event.parcelEvent === 'return' ? (
                      <>
                        <dt>Outcome</dt>
                        <dd>Returned to the carrier — not collected</dd>
                      </>
                    ) : (
                      event.direction === 'out' && (
                        <>
                          <dt>Picked up by</dt>
                          <dd>
                            {event.parcelCollector || 'Not recorded'}
                            {event.parcelCollector
                              ? collectorNote(event.parcelCollectorResolution)
                              : ''}
                          </dd>
                        </>
                      )
                    )}
                  </>
                ) : (
                  event.people.length > 0 && (
                    <>
                      <dt>People</dt>
                      <dd>{event.people.join(', ')}</dd>
                    </>
                  )
                )}

                {event.unit && (
                  <>
                    <dt>Unit</dt>
                    <dd>{event.unit}</dd>
                  </>
                )}

                <dt>Type</dt>
                <dd>{event.kind}</dd>

                <dt>Recorded by</dt>
                <dd>
                  {/* THE ROLE IS A LABEL, NOT THE STORED SLUG. This rendered
                      "Manager 1, property_manager" — a database value on a
                      screen, the same class of leak as a raw id. roleLabel is
                      the one place that mapping lives, and normalizeUserRole is
                      what decides whether the stored value is a role at all. */}
                  {event.actor?.name || 'Unknown'}
                  {event.actor?.name && normalizeUserRole(event.actor.role)
                    ? `, ${roleLabel(event.actor.role)}`
                    : ''}
                </dd>

                {event.departedAt && (
                  <>
                    {/* An activity's end rides the same field as a visitor's
                        departure; it was closed, nobody "departed". */}
                    <dt>{event.source === 'daily_log' ? 'Closed' : 'Departed'}</dt>
                    <dd>
                      {when(event.departedAt, clock.zone)}
                      <BothClocks
                        iso={event.departedAt}
                        clock={clock}
                        buildingZone={event.tz || answered?.buildingZone}
                      />
                    </dd>
                  </>
                )}

                {event.text.filter(Boolean).length > 0 && (
                  <>
                    <dt>Notes</dt>
                    <dd>{event.text.filter(Boolean).join(' — ')}</dd>
                  </>
                )}
              </Rows>
            </Card>
          ))}

          {/* ON A VISITOR PASS (resident-portal Plan 3, RP-3.5). Whose pass it
              was, and whether the sign-in fell outside its window — the two
              facts an owner auditing an arrival needs. "View pass" opens the
              pass over this record for anyone who may read passes; a pass that
              retention has since removed says so and offers nothing to open. */}
          {passLine && source === 'visitor_log' ? (
            <Card {...sectionMotion((events?.length ?? 0) + 1)} aria-label="Visitor pass">
              <PassPills>
                <StatusPill tone="neutral">
                  {!passLine.pass_id
                    ? 'On a pass (since removed)'
                    : passLine.created_via === 'resident'
                      ? 'On a resident pass'
                      : 'On a pass added by the building'}
                </StatusPill>
                {passLine.window_state === 'early' || passLine.window_state === 'late' ? (
                  <StatusPill tone="warning">
                    {passLine.window_state === 'early' ? 'Early' : 'Late'}
                  </StatusPill>
                ) : null}
              </PassPills>
              {passLine.pass_id && can(permissions, PERMISSIONS.VISITORPASS_READ) ? (
                <Actions>
                  <AppButton variant="ghost" onClick={() => setPassOpen(true)}>
                    View pass
                  </AppButton>
                </Actions>
              ) : null}
            </Card>
          ) : null}

          {/* TAKE IT OUT (owner, 2026-09-11). Renders nothing unless this is an
              in that is still open and the reader may record the out. */}
          {events ? (
            <QuickOut
              source={source}
              id={id}
              events={events}
              order={events.length + 1}
              onDone={() => {
                // Re-read THIS record (it now carries its out) and tell the
                // host, whose list has just gained a row.
                void load();
                if (onChanged) onChanged();
              }}
            />
          ) : null}

          {/* CORRECTING THE RECORD (owner: "allow edits").
              Writes through the EXISTING log endpoints — no second write path —
              which are permission-gated and already append to
              entry_field_edits, so every change made here appears in the trail
              below without any extra machinery. Names are deliberately not
              editable: they carry provenance and an occupancy resolution that
              the desk capture flow owns. */}
          {editable && mayEdit && (
            <Card {...sectionMotion((events?.length ?? 0) + 1)}>
              <Text
                mt={0}
                mb={0}
                fontSize={16}
                fontWeight={700}
                fontFamily={font.family}
              >
                Correct this entry
              </Text>

              {!editing && (
                <Actions>
                  <Ghost type="button" onClick={() => setEditing(true)}>
                    Edit
                  </Ghost>
                </Actions>
              )}

              {editing && (
                <>
                  <EditGrid>
                    {Object.keys(editable).map(field => (
                      <LabelledField key={field}>
                        {EDIT_LABEL[field] || field}
                        {LONG_FIELDS.has(field) ? (
                          <textarea
                            value={String(draft[field] ?? '')}
                            onChange={e =>
                              setDraft(d => ({ ...d, [field]: e.target.value }))
                            }
                          />
                        ) : (
                          <input
                            value={String(draft[field] ?? '')}
                            onChange={e =>
                              setDraft(d => ({ ...d, [field]: e.target.value }))
                            }
                          />
                        )}
                      </LabelledField>
                    ))}
                  </EditGrid>
                  <Actions>
                    <Primary
                      type="button"
                      disabled={saving || pendingChanges.length === 0}
                      onClick={() => setReviewing(true)}
                    >
                      {pendingChanges.length === 0
                        ? 'No changes yet'
                        : `Review ${pendingChanges.length} change${
                          pendingChanges.length === 1 ? '' : 's'
                        }`}
                    </Primary>
                    <Ghost
                      type="button"
                      disabled={saving}
                      onClick={() => {
                        setDraft(editable);
                        setReviewing(false);
                        setEditing(false);
      setReviewing(false);
                      }}
                    >
                      Cancel
                    </Ghost>
                  </Actions>

                  {/* ---- THE CONFIRMATION, FIELD BY FIELD ----
                      Save used to go straight through, so a mis-keyed field
                      became a permanent, attributed correction with no moment
                      to catch it. This is that moment: exactly the fields the
                      request will carry, each with the value it is replacing.

                      Nothing here can disagree with what is sent — the list and
                      the patch are derived from the same comparison. */}
                  {reviewing && (
                    <Diff>
                      <Text
                        as="p"
                        mt={0}
                        mb={8}
                        fontSize={13}
                        fontWeight={700}
                        fontFamily={font.family}
                      >
                        Confirm this correction
                      </Text>
                      {pendingChanges.map(change => (
                        <DiffRow key={change.field}>
                          <b>{change.label}</b>
                          <s>{change.from || '(empty)'}</s>
                          <span>{change.to || '(empty)'}</span>
                        </DiffRow>
                      ))}
                      <Actions>
                        <Primary type="button" disabled={saving} onClick={save}>
                          {saving ? 'Saving…' : 'Confirm and save'}
                        </Primary>
                        <Ghost
                          type="button"
                          disabled={saving}
                          onClick={() => setReviewing(false)}
                        >
                          Keep editing
                        </Ghost>
                      </Actions>
                    </Diff>
                  )}
                  <Muted>
                    Every change is recorded below with who made it and what the
                    value was before. Nothing is overwritten silently, and the
                    previous value is never lost — which is what makes a
                    correction safe to make.
                  </Muted>
                </>
              )}
            </Card>
          )}

          {/* THE EDIT TRAIL (brief §11). Historical activity is never silently
              rewritten: entry_field_edits is append-only by trigger, so what is
              shown here cannot have been tidied up afterwards. */}
          <Card {...sectionMotion((events?.length ?? 0) + 2)}>
            <Text mt={0} mb={0} fontSize={16} fontWeight={700} fontFamily={font.family}>
              Edit history
            </Text>

            {!history?.edited && !history?.actions.length && (
              <Muted>This record has not been edited.</Muted>
            )}

            {history && history.edits.length > 0 && (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {history.edits.map((edit, i) => (
                  <HistoryItem key={`${edit.field}-${edit.at}-${i}`}>
                    <strong>{FIELD_LABEL[edit.field] || edit.field}</strong>{' '}
                    changed{' '}
                    {edit.previousReadable ? (
                      <>
                        from <Was>{edit.previous || '—'}</Was>{' '}
                      </>
                    ) : (
                      // Never the ciphertext. An unreadable superseded value is
                      // reported as unavailable.
                      <>from an earlier value that can no longer be read </>
                    )}
                    to <strong>{edit.currentReadable ? edit.current : '—'}</strong>
                    <br />
                    <Muted as="span">
                      {edit.actor?.name || 'Unknown'} · {when(edit.at, clock.zone)}
                      {edit.fromSource === 'scan' && edit.toSource === 'scan_edited'
                        ? ' · corrected a scanned value'
                        : ''}
                    </Muted>
                  </HistoryItem>
                ))}
              </ul>
            )}

            {history && history.actions.length > 0 && (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {history.actions.map((action, i) => (
                  <HistoryItem key={`${action.action}-${action.at}-${i}`}>
                    <strong>{action.action.replace(/[._]/g, ' ')}</strong>
                    <br />
                    <Muted as="span">
                      {action.actor?.name || 'Unknown'} · {when(action.at, clock.zone)}
                      {action.changedFields?.length
                        ? ` · changed ${action.changedFields.join(', ')}`
                        : ''}
                    </Muted>
                  </HistoryItem>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}

      <PassDetailModal
        passId={passLine?.pass_id ?? null}
        open={passOpen && !!passLine?.pass_id}
        onClose={() => setPassOpen(false)}
        onChanged={onChanged}
      />
    </Page>
  );
};

const PassPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
`;
